import { Chip, Tooltip } from '@mui/material';
import { dateTimeNoSeconds, shortDay } from '../../shared/dateTime';
import { FEATURE_TMS_EXPORT_ONE_STEP } from '../../shared/status';
import { useFeature } from '../featureFlags/FeatureGate';

/**
 * Etap prezlecenia w banku i na karcie zlecenia (makieta back office v2,
 * BR-ADM-15, ścieżka FTL-03; 20.10.2026, przełącznik `preOrderStageInBank`).
 *
 * FTL i Trade widzą, na jakim etapie przyjęcia jest zlecenie z prezlecenia
 * i w którym dziale — bez edycji. Etap liczy API (`pre-order/pre-order-stage.ts`)
 * i niesie go tylko z przełącznikiem: bez pola w odpowiedzi znacznika
 * i sekcji nie ma, więc wersja „obecna” wygląda jak produkcja.
 *
 * Bez react-admina — znacznik siedzi w komórce listy (`listCells.tsx`);
 * sekcja karty: `PreOrderStageSection.tsx`.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_PRE_ORDER_STAGE = 'preOrderStageInBank';

export type PreOrderStageCode = 'draft' | 'approved' | 'file' | 'inTms';

export interface PreOrderStageView {
  code: PreOrderStageCode;
  label: string;
  at: string | null;
  atLabel: string;
  detail: string;
  failed: boolean;
  department: string | null;
  preOrderId: number;
  orderNumber: string | null;
  steps: Array<{ code: PreOrderStageCode; label: string; at: string | null; current: boolean }>;
}

/**
 * Wartości filtra „Etap prezlecenia” — kontrakt z API (`preOrderStage`,
 * react-admin je pamięta). Etykiety jak etapy w API.
 */
export const PRE_ORDER_STAGE_CHOICES = [
  { id: 'any', name: 'Z prezlecenia (każdy etap)' },
  { id: 'draft', name: 'Szkic' },
  { id: 'approved', name: 'Zatwierdzone' },
  { id: 'file', name: 'Plik wytworzony' },
  { id: 'inTms', name: 'Jest w TMS' },
];

/** Krótka nazwa do znacznika w wierszu banku (lista mieści się przy 1366 px). */
const SHORT_LABELS: Record<PreOrderStageCode, string> = {
  draft: 'Szkic',
  approved: 'Zatwierdzone',
  file: 'Plik',
  inTms: 'Jest w TMS',
};

/**
 * Krótkie nazwy z paczki p. Mariusza 21.10 (BR-ADM-10; przełącznik
 * `tmsExportOneStep`): „Wysłane” i „Zapisane w TMS”; filtr — te same kody.
 */
const SHORT_LABELS_TMS_EXPORT: Record<PreOrderStageCode, string> = {
  ...SHORT_LABELS,
  file: 'Wysłane',
  inTms: 'Zapisane w TMS',
};

/**
 * Krótka nazwa przy błędzie zapisu (integracja 22.10.2026): znacznik mówi
 * o błędzie, nie o etapie — jak kolejka „Błąd zapisu do VIA” i pełna nazwa
 * z API (`PRE_ORDER_STAGE_FAILED_LABEL`). Wcześniej czerwone „Wysłane”.
 */
export const SHORT_LABEL_FAILED = 'Błąd zapisu';

/** Wartości filtra z nazwami z paczki 21.10 (przełącznik `tmsExportOneStep`). */
export const PRE_ORDER_STAGE_CHOICES_TMS_EXPORT = PRE_ORDER_STAGE_CHOICES.map((c) =>
  c.id === 'file' ? { ...c, name: 'Wysłane do TMS' } : c.id === 'inTms' ? { ...c, name: 'Zapisane w TMS' } : c,
);

export const PRE_ORDER_STAGE_COLORS: Record<PreOrderStageCode, 'default' | 'info' | 'primary' | 'success'> = {
  draft: 'default',
  approved: 'info',
  file: 'primary',
  inTms: 'success',
};

/** Etap z wiersza API; brak albo kształt nieznany — `null`. */
export function preOrderStageOf(record: Record<string, unknown> | undefined): PreOrderStageView | null {
  const s = record?.preOrderStage as PreOrderStageView | null | undefined;
  return s && typeof s === 'object' && typeof s.code === 'string' ? s : null;
}

/** Dymek znacznika: etap, data z jej znaczeniem, dział, zdanie z API. */
export function preOrderStageHint(s: PreOrderStageView): string {
  const when = s.at ? `${s.atLabel} ${dateTimeNoSeconds(s.at)}` : `${s.atLabel}, bez daty`;
  const dept = s.department ? ` Dział: ${s.department}.` : '';
  return `Prezlecenie — ${s.label} (${when}).${dept} ${s.detail}`;
}

/** Znacznik etapu w wierszu banku: „Zatwierdzone · 20.10”. */
export const PreOrderStageChip = ({ record }: { record: Record<string, unknown> }) => {
  const tmsNames = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  const s = preOrderStageOf(record);
  if (!s) return null;
  const day = s.at ? shortDay(s.at) : null;
  return (
    <Tooltip title={preOrderStageHint(s)}>
      <Chip
        size="small"
        variant="outlined"
        color={s.failed ? 'error' : PRE_ORDER_STAGE_COLORS[s.code] ?? 'default'}
        label={`${s.failed ? SHORT_LABEL_FAILED : (tmsNames ? SHORT_LABELS_TMS_EXPORT : SHORT_LABELS)[s.code] ?? s.label}${day && day !== '—' ? ` · ${day}` : ''}`}
        data-testid="pre-order-stage-chip"
        data-stage={s.code}
      />
    </Tooltip>
  );
};
